import { useState } from "react";
import { Alert, Button, Skeleton } from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { IconButton } from "@outpave/ds";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DashboardCard } from "hooks/useEmployeeDashboard";

const STORAGE_KEY = "DISMISSED_ISSUED_CARD_IDS";

interface Props {
  /** Cards in `ISSUED` state — drives both visibility and the dismissed set. */
  issuedCards: DashboardCard[];
  /** True when the `issuedCards` query failed. */
  hasError: boolean;
  /** True while the `issuedCards` query is in flight. Shows a skeleton. */
  isLoading: boolean;
}

/**
 * Returns the `localStorage` object only if it's actually usable. Guards
 * against: non-browser renders (SSR, tests) where the global is undefined,
 * sandboxed iframes where the property access itself throws `SecurityError`,
 * and Safari private mode where the object exists but `setItem` throws.
 */

const readDismissed = (): string[] => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return [];
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((v): v is string => typeof v === "string")
      : [];
  } catch {
    return [];
  }
};

/**
 * ActivateCardBanner — promo banner prompting the employee to activate any
 * newly issued cards.
 *
 * ## Visibility
 * Shows whenever at least one card in `issuedCards` has not yet been
 * dismissed. Dismissal is per-card-id, persisted in `localStorage` under
 * `DISMISSED_ISSUED_CARD_IDS` as a JSON array of strings. Clicking the close
 * button merges the IDs of every currently-issued card into that set, so the
 * banner stays hidden until the next time the user is assigned a new issued
 * card — at which point the unseen ID re-triggers it.
 *
 * While the underlying query is loading, a skeleton placeholder is rendered
 * in place of the banner to keep the dashboard layout stable. On error, the
 * component renders nothing (fail-closed).
 *
 * ## Data flow
 * `issuedCards` is sourced from `useEmployeeDashboard` (cards with status
 * `ISSUED`). When the array is empty or every ID is already dismissed, the
 * component renders nothing.
 *
 * ## i18n
 * All copy is translated via `react-i18next`. The component owns the
 * banner's title and action label; nothing else is user-facing.
 */
const ActivateCardBanner = ({ issuedCards, hasError, isLoading }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [dismissedIds, setDismissedIds] = useState<string[]>(readDismissed);

  if (hasError) return null;

  if (isLoading) {
    return (
      // Holds the banner's row: the Alert's vertical padding around the action
      // button, which is the tallest thing in it (the DS pins medium at 38).
      <Skeleton
        variant="rounded"
        width="100%"
        sx={{
          height: "calc(var(--space-4) * 2 + 38px)",
          borderRadius: "var(--radius-md)",
        }}
      />
    );
  }

  const issuedIds = issuedCards.map((c) => c.id);
  const hasUndismissed = issuedIds.some((id) => !dismissedIds.includes(id));
  if (!hasUndismissed) return null;

  const handleClose = () => {
    const next = Array.from(new Set([...dismissedIds, ...issuedIds]));
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      }
    } catch {
      // ignore storage failures (fail-closed)
    }
    setDismissedIds(next);
  };

  return (
    // Both controls sit in `action`: MUI renders its built-in close button only
    // when `action` is null, so passing `onClose` beside one is silently dropped.
    <Alert
      severity="info"
      role="status"
      action={
        <>
          <Button
            variant="default-outlined"
            onClick={() => navigate("/my/activate")}
          >
            {t("Activate card")}
          </Button>
          <IconButton
            variant="ghost"
            aria-label={t("Close")}
            onClick={handleClose}
          >
            <HugeiconsIcon icon={Cancel01Icon} size={16} />
          </IconButton>
        </>
      }
    >
      {t("Ready to start using your card?")}
    </Alert>
  );
};

export default ActivateCardBanner;
